import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, ArrowUpDown, Save } from "lucide-react";
import { useFinance, Modal, Field, inputCls, kicker, Loading, EmptyState, fmt, pct, HEALTH_TONE, downloadCsv } from "@/admin/finance/financeUtils";

const SORTS = [["date", "Newest"], ["profit", "Highest profit"], ["worst", "Lowest profit"], ["revenue", "Highest revenue"], ["margin", "Worst margin"], ["labor", "Most labor"]];

function VarRow({ label, v, money = true }) {
  if (!v) return null;
  const f = money ? fmt : (x) => x;
  const up = v.diff > 0;
  return (
    <tr className="border-b border-[color:var(--border-subtle)]/40">
      <td className="py-2 text-secondary">{label}</td>
      <td className="py-2 text-right text-white tabular-nums">{f(v.estimate)}</td>
      <td className="py-2 text-right text-white tabular-nums">{f(v.actual)}</td>
      <td className={`py-2 text-right tabular-nums ${up ? "text-amber-300" : "text-emerald-300"}`}>{v.diff > 0 ? "+" : ""}{f(v.diff)}{v.pct !== null ? ` (${v.pct > 0 ? "+" : ""}${v.pct}%)` : ""}</td>
    </tr>
  );
}

function JobDetail({ jobId, onClose, onChanged }) {
  const { data, loading, reload } = useFinance(`/finance/job/${jobId}`, [jobId]);
  const [ov, setOv] = useState({ final_price: "", discount: "", discount_reason: "" });
  const saveOverride = async () => {
    const patch = {};
    if (ov.final_price !== "") patch.final_price = Number(ov.final_price);
    if (ov.discount !== "") patch.discount = Number(ov.discount);
    if (ov.discount_reason) patch.discount_reason = ov.discount_reason;
    if (!Object.keys(patch).length) { toast.error("Nothing to save"); return; }
    try { await api.put(`/finance/job/${jobId}/finance`, patch); toast.success("Job finance updated"); reload(); onChanged?.(); }
    catch (e) { console.error(e); toast.error("Could not save"); }
  };
  const f = data?.finance;
  return (
    <Modal open onClose={onClose} title={f ? `${f.ref} — ${f.client_name}` : "Job finance"} testid="finance-job-detail">
      {loading && <Loading />}
      {f && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`rounded-full border px-3 py-1 text-xs ${HEALTH_TONE[f.health]}`} data-testid="job-health">{f.health}</span>
            <span className="text-sm text-muted-c">Margin {pct(f.margin)}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Revenue", f.revenue], ["Total cost", f.total_cost], ["Profit", f.profit], ["Collected", f.net_collected], ["Outstanding", f.outstanding], ["Labor", f.labor_cost], ["Expenses", f.expenses], ["Rev / hr", f.revenue_per_labor_hour]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-[color:var(--border-subtle)] bg-black/30 p-3"><p className="text-[10px] uppercase tracking-wide text-muted-c">{k}</p><p className="mt-1 text-white tabular-nums">{fmt(v)}</p></div>
            ))}
          </div>

          {/* Estimate vs actual */}
          <div>
            <p className={kicker}>Estimate vs actual</p>
            <table className="mt-2 w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wide text-muted-c"><th className="text-left py-1"></th><th className="text-right py-1">Estimate</th><th className="text-right py-1">Actual</th><th className="text-right py-1">Variance</th></tr></thead>
              <tbody>
                <VarRow label="Labor hours" v={f.estimate_vs_actual.hours} money={false} />
                <VarRow label="Cost" v={f.estimate_vs_actual.cost} />
                <VarRow label="Price" v={f.estimate_vs_actual.price} />
                <VarRow label="Profit" v={f.estimate_vs_actual.profit} />
              </tbody>
            </table>
          </div>

          {/* Manual override */}
          <div className="rounded-xl border border-[color:var(--border-strong)] bg-white/[0.02] p-4">
            <p className={kicker}>Adjust price / discount</p>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <Field label={`Final price (now ${fmt(f.revenue)})`}><input type="number" className={inputCls} value={ov.final_price} onChange={(e) => setOv({ ...ov, final_price: e.target.value })} data-testid="job-final-price" /></Field>
              <Field label="Discount"><input type="number" className={inputCls} value={ov.discount} onChange={(e) => setOv({ ...ov, discount: e.target.value })} /></Field>
              <Field label="Reason"><input className={inputCls} value={ov.discount_reason} onChange={(e) => setOv({ ...ov, discount_reason: e.target.value })} /></Field>
            </div>
            <button onClick={saveOverride} data-testid="job-finance-save" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-black hover:bg-gold-soft"><Save className="h-4 w-4" /> Save</button>
          </div>

          {/* Linked records */}
          <div className="grid sm:grid-cols-3 gap-3 text-xs">
            <div><p className="text-muted-c mb-1">Payments ({data.payments.length})</p>{data.payments.map((p) => <p key={p.id} className="text-secondary">{p.date} · {p.kind} {fmt(p.amount)}</p>)}{!data.payments.length && <p className="text-muted-c">None</p>}</div>
            <div><p className="text-muted-c mb-1">Worker hours ({data.shifts.length})</p>{data.shifts.map((sh) => <p key={sh.id} className="text-secondary">{sh.worker_name || "—"} · {sh.paid_hours_calc}h · {fmt(sh.total_owed)}</p>)}{!data.shifts.length && <p className="text-muted-c">None</p>}</div>
            <div><p className="text-muted-c mb-1">Expenses ({data.expenses.length})</p>{data.expenses.map((ex) => <p key={ex.id} className="text-secondary">{ex.category} · {fmt(ex.amount)}</p>)}{!data.expenses.length && <p className="text-muted-c">None</p>}</div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function FinanceJobs() {
  const [sort, setSort] = useState("date");
  const { data, loading, reload } = useFinance(`/finance/jobs?sort=${sort}`, [sort]);
  const [openId, setOpenId] = useState(null);

  return (
    <div data-testid="finance-jobs">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2"><ArrowUpDown className="h-4 w-4 text-gold-soft" /><select className={`${inputCls} w-auto`} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="jobs-sort">{SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <button onClick={() => downloadCsv("/finance/export/jobs", "ascendia-job-profit.csv")} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 text-xs text-white hover:bg-white/5"><Download className="h-3.5 w-3.5" /> CSV</button>
      </div>
      {loading && <Loading />}
      {data && data.length === 0 && <EmptyState label="No jobs yet." />}
      {data && data.length > 0 && (
        <div className="ascendia-card p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-c border-b border-[color:var(--border-subtle)]"><th className="p-3">Job</th><th className="p-3">Client</th><th className="p-3 text-right">Revenue</th><th className="p-3 text-right">Labor</th><th className="p-3 text-right">Expenses</th><th className="p-3 text-right">Profit</th><th className="p-3 text-right">Margin</th><th className="p-3 text-right">Hours</th><th className="p-3">Health</th></tr></thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.job_id} onClick={() => setOpenId(r.job_id)} className="border-b border-[color:var(--border-subtle)]/40 cursor-pointer hover:bg-white/[0.02]" data-testid="finance-job-row">
                  <td className="p-3 text-gold-soft whitespace-nowrap">{r.ref}</td>
                  <td className="p-3 text-white">{r.client_name}<span className="block text-[10px] text-muted-c">{r.service}</span></td>
                  <td className="p-3 text-right text-white tabular-nums">{fmt(r.revenue)}</td>
                  <td className="p-3 text-right text-secondary tabular-nums">{fmt(r.labor_cost)}</td>
                  <td className="p-3 text-right text-secondary tabular-nums">{fmt(r.expenses)}</td>
                  <td className={`p-3 text-right tabular-nums ${r.profit < 0 ? "text-rose-300" : "text-white"}`}>{fmt(r.profit)}</td>
                  <td className="p-3 text-right text-secondary tabular-nums">{pct(r.margin)}</td>
                  <td className="p-3 text-right text-secondary tabular-nums">{r.labor_hours}</td>
                  <td className="p-3"><span className={`rounded-full border px-2 py-0.5 text-[10px] ${HEALTH_TONE[r.health]}`}>{r.health}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openId && <JobDetail jobId={openId} onClose={() => setOpenId(null)} onChanged={reload} />}
    </div>
  );
}
